import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Box, Container, Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import ReservationModal from '../components/ReservationModal';
import { usePrimaryLocation } from '../hooks/usePrimaryLocation';

interface MainLayoutProps {
  children: React.ReactNode;
}

// Utility function to format phone number
const formatPhoneForDisplay = (phone?: string): string => {
  if (!phone) return '(555) 123-4567'; // Fallback
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  return phone;
};

// Utility function to format address
const formatAddress = (location: any): string => {
  if (!location) return '1 Fallen Leaf Road, South Lake Tahoe, CA'; // Fallback
  
  const parts = [];
  if (location.address) parts.push(location.address);
  if (location.city) parts.push(location.city);
  if (location.state) parts.push(location.state);
  if (location.zipCode) parts.push(location.zipCode);
  
  return parts.length > 0 ? parts.join(', ') : '1 Fallen Leaf Road, South Lake Tahoe, CA';
};

const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const [headerHidden, setHeaderHidden] = useState(false);
  const [mainNavTop, setMainNavTop] = useState(false);
  const [bookingModalOpen, setBookingModalOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [ctaPulse, setCtaPulse] = useState(false);
  
  // Get primary location data
  const { primaryLocation, loading, error } = usePrimaryLocation();
  
  useEffect(() => {
    const handleScroll = () => {
      // Handle header visibility on scroll
      if (window.scrollY > 100) {
        setHeaderHidden(true);
        setMainNavTop(true);
      } else {
        setHeaderHidden(false);
        setMainNavTop(false);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Add pulse effect to CTA button periodically
  useEffect(() => {
    const pulseInterval = setInterval(() => {
      setCtaPulse(true);
      setTimeout(() => setCtaPulse(false), 2000);
    }, 10000); // Pulse every 10 seconds

    return () => clearInterval(pulseInterval);
  }, []);

  // Sync mobile menu state with DOM
  useEffect(() => {
    const navLinks = document.querySelector('.nav-links');
    const mobileToggle = document.querySelector('.mobile-menu-toggle');
    
    if (navLinks && mobileToggle) {
      if (mobileMenuOpen) {
        navLinks.classList.add('active');
        mobileToggle.classList.add('active');
      } else {
        navLinks.classList.remove('active');
        mobileToggle.classList.remove('active');
      }
    }
  }, [mobileMenuOpen]);

  const handleOpenBookingModal = (e: React.MouseEvent) => {
    e.preventDefault();
    setBookingModalOpen(true);
    setMobileMenuOpen(false); // Close mobile menu when opening booking modal
  };

  const handleCloseBookingModal = () => {
    setBookingModalOpen(false);
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  // Get contact information with fallbacks
  const contactPhone = formatPhoneForDisplay(primaryLocation?.phone);
  const contactEmail = primaryLocation?.email || 'info@fallenleaflodge.com';
  const contactAddress = formatAddress(primaryLocation);

  return (
    <>
      <header className="header-enhanced">
        <div className={`header-top ${headerHidden ? 'header-top-hidden' : ''}`}>
          <div className="container">
            <div className="contact-info">
              <span><i className="fas fa-phone"></i> {contactPhone}</span>
              <span><i className="fas fa-envelope"></i> {contactEmail}</span>
            </div>
            <div className="weather-widget">
              <div className="weather-info">
                <div className="weather-item temp-widget-container">
                  <div className="temp-widget">
                    <span className="temp-label">Air</span>
                    <span className="temp-value" id="temperature">--°F</span>
                  </div>
                </div>
                <div className="weather-item temp-widget-container">
                  <div className="temp-widget">
                    <span className="temp-label">Water</span>
                    <span className="temp-value" id="water-temp">--°F</span>
                  </div>
                </div>
                <div className="weather-item wind-widget-container">
                  <div className="comprehensive-wind-widget" id="comprehensive-wind-widget">
                    <div className="wind-gauge">
                      <div className="gauge-ring">
                        <div className="gauge-ticks"></div>
                        <div className="wind-direction-arrow" id="wind-direction-arrow"></div>
                      </div>
                      <div className="wind-center-content">
                        <div className="current-wind-speed" id="current-wind-speed">--</div>
                        <div className="wind-speed-unit">mph</div>
                        <div className="wind-peak-label">Today's Peak:</div>
                        <div className="wind-peak-value" id="wind-peak-value">--</div>
                      </div>
                    </div>
                    <div className="wind-details">
                      <div className="wind-from">
                        <span className="wind-from-label">From</span>
                        <span className="wind-from-direction" id="wind-from-direction">--</span>
                      </div>
                      <div className="wind-gusts">
                        <span className="wind-gusts-label">Gusts</span>
                        <span className="wind-gusts-value" id="wind-gusts-value">--</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <nav className={`main-nav ${mainNavTop ? 'main-nav-top' : ''}`}>
          <div className="container">
            <div className="logo">
              <Link to="/" onClick={closeMobileMenu}>
                <img src="/images/logo.png" alt="Fallen Leaf Lodge Logo" />
              </Link>
            </div>
            <ul className={`nav-links ${mobileMenuOpen ? 'active' : ''}`}>
              <li><Link to="/" onClick={closeMobileMenu}>Home</Link></li>
              <li><Link to="/about" onClick={closeMobileMenu}>About</Link></li>
              <li><Link to="/marina" onClick={closeMobileMenu}>Marina</Link></li>
              <li><Link to="/cafe" onClick={closeMobileMenu}>Cafe</Link></li>
              <li><Link to="/store" onClick={closeMobileMenu}>Store</Link></li>
              <li><Link to="/contact" onClick={closeMobileMenu}>Contact</Link></li>
              <li className="cta-button">
                <a 
                  href="#" 
                  onClick={handleOpenBookingModal} 
                  className={`btn btn-small ${ctaPulse ? 'pulse' : ''}`}
                >
                  <i className="fas fa-calendar-alt"></i> Boat Reservations
                </a>
              </li>
            </ul>
            <div className="mobile-menu-toggle" onClick={toggleMobileMenu}>
              <i className={`fas ${mobileMenuOpen ? 'fa-times' : 'fa-bars'}`}></i>
            </div>
          </div>
        </nav>
      </header>
      
      <main>
        {children}
      </main>
      
      <footer>
        <div className="footer-main">
          <div className="container">
            <div className="footer-grid">
              <div className="footer-column">
                <div className="footer-logo">
                  <img src="/images/logo.png" alt="Fallen Leaf Lodge Logo" />
                  <p>Your gateway to adventure on Fallen Leaf Lake</p>
                </div>
              </div>
              
              <div className="footer-column">
                <h4>Business Hours</h4>
                <div className="business-hours">
                  <div className="business-item">
                    <h3>General Store</h3>
                    <p>Closed for the Season</p>
                    <p>Open May - October</p>
                  </div>
                  
                  <div className="business-item">
                    <h3>Cafe</h3>
                    <p>Closed for the Season</p>
                    <p>Open May - October</p>
                  </div>
                  
                  <div className="business-item">
                    <h3>Marina</h3>
                    <p>Closed for the Season</p>
                    <p>Open May - October</p>
                  </div>
                </div>
              </div>
              
              <div className="footer-column">
                <h4>Contact Us</h4>
                <div className="contact-column">
                  <div className="contact-item">
                    <i className="fas fa-map-marker-alt"></i>
                    <p>400 Fallen Leaf Rd. South<br />Lake Tahoe, CA 96150</p>
                  </div>
                  
                  <div className="contact-item">
                    <i className="fas fa-phone"></i>
                    <p>{contactPhone}</p>
                  </div>
                  
                  <div className="contact-item">
                    <i className="fas fa-envelope"></i>
                    <p>{contactEmail}</p>
                  </div>
                  
                  <div className="social-links">
                    <a href="#" aria-label="Facebook"><i className="fab fa-facebook-f"></i></a>
                    <a href="#" aria-label="Instagram"><i className="fab fa-instagram"></i></a>
                    <a href="#" aria-label="Twitter"><i className="fab fa-twitter"></i></a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Mobile Weather Widget - Only visible on mobile */}
        <div className="footer-weather-mobile">
          <div className="container">
            <h4>Current Conditions</h4>
            <div className="mobile-weather-info">
              <div className="mobile-weather-item">
                <i className="fas fa-temperature-high"></i>
                <span>Air: <span id="footer-mobile-temperature">--°F</span></span>
              </div>
              <div className="mobile-weather-item">
                <i className="fas fa-water"></i>
                <span>Water: <span id="footer-mobile-water-temp">--°F</span></span>
              </div>
              <div className="mobile-weather-item">
                <i className="fas fa-wind"></i>
                <span>Wind: <span id="footer-mobile-wind-speed">--</span> <span id="footer-mobile-wind-direction">--</span></span>
              </div>
            </div>
          </div>
        </div>
        
        <div className="footer-bottom">
          <div className="container">
            <p>&copy; {new Date().getFullYear()} Fallen Leaf Lodge. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* Booking Modal */}
      <ReservationModal open={bookingModalOpen} onClose={handleCloseBookingModal} />
    </>
  );
};

export default MainLayout; 